<template>
  <div class="checkbox-wrapper">
    <div
      class="flex w-full justify-between"
      :class="[darkMode ? 'text-white lg:text-text-dark' : 'text-text-dark']"
    >
      <span v-if="hasLabel" :for="label" class="mb-1 text-sm font-bold">{{ label }} </span>
      <span v-if="optional && hasLabel" class="text-xs">[{{ optional }}]</span>
    </div>

    <input
      :id="id"
      v-model="modelValue"
      type="checkbox"
      class="checkbox-input"
      :aria-describedby="isErrored ? `${id}-error` : undefined"
      :aria-invalid="isErrored ? 'true' : 'false'"
      :aria-checked="modelValue ? 'true' : 'false'"
      aria-label="checkbox"
    />
    <label
      :for="id"
      class="checkbox-label"
      :class="[darkMode ? 'text-white lg:text-text-dark' : 'text-text-dark']"
    >
      <span class="checkbox-custom" :class="[isErrored ? 'error' : '']">
        <BaseIcon v-if="modelValue" icon="Check" class="checkbox-icon" aria-hidden="true" />
      </span>
      {{ value }}
      <span v-if="optional && !hasLabel" class="text-xs">[{{ optional }}]</span>
    </label>
    <span v-if="isErrored && errorText" :id="`${id}-error`" class="error-text w-full">{{
      errorText
    }}</span>
  </div>
</template>

<script setup lang="ts">
import BaseIcon from '../BaseIcon/BaseIcon.vue'

const modelValue = defineModel<boolean>()

defineProps<{
  id: string
  label: string
  value?: string
  errorText?: string
  isErrored?: boolean
  optional?: '' | string
  hasLabel?: false | boolean
  darkMode?: boolean
}>()
</script>

<style scoped>
.checkbox-wrapper {
  @apply flex flex-wrap items-center gap-2;
}

.checkbox-input {
  @apply absolute h-0 w-0 opacity-0;
}

.checkbox-label {
  @apply relative flex cursor-pointer items-center gap-2 text-sm;
}

.checkbox-custom {
  @apply flex h-5 w-5 shrink-0 items-center justify-center rounded-xs rounded-tl-none border-2 border-brand-02 bg-background-03 transition-all focus:outline-offset-2 focus:outline-brand-02;
}

.checkbox-input:checked + .checkbox-label .checkbox-custom {
  @apply border-brand-01 bg-brand-01;
}

.checkbox-icon {
  @apply h-4 w-4 text-text-light;
}

.checkbox-input:focus + .checkbox-label .checkbox-custom {
  @apply outline outline-2 outline-offset-2 outline-brand-02;
}

.checkbox-custom.error {
  @apply border-brand-02;
}

.checkbox-input:checked + .checkbox-label .checkbox-custom.error {
  @apply border-states-error bg-states-error;
}

.checkbox-input:focus + .checkbox-label .checkbox-custom.error {
  @apply outline outline-2 outline-offset-2 outline-states-error;
}

.error-text {
  @apply mt-1 text-xs text-states-error;
}
</style>
